// Republic Atlas - Candidate Hooks

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { candidateService, type CandidateFilters } from '@/services';

// Cache times
const CANDIDATE_STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours
const CANDIDATE_CACHE_TIME = 48 * 60 * 60 * 1000; // 48 hours

/**
 * Hook to fetch all candidates with filters
 */
export function useCandidates(filters?: CandidateFilters) {
  return useQuery({
    queryKey: ['candidates', filters],
    queryFn: () => candidateService.getCandidates(filters),
    staleTime: CANDIDATE_STALE_TIME,
    gcTime: CANDIDATE_CACHE_TIME,
  });
}

/**
 * Hook to fetch a single candidate by ID
 */
export function useCandidate(id: string | undefined) {
  return useQuery({
    queryKey: ['candidate', id],
    queryFn: () => candidateService.getCandidate(id!),
    enabled: !!id,
    staleTime: CANDIDATE_STALE_TIME,
    gcTime: CANDIDATE_CACHE_TIME,
  });
}

/**
 * Hook to fetch candidates for a specific office
 */
export function useCandidatesByOffice(officeId: string | undefined) {
  return useQuery({
    queryKey: ['candidates', 'office', officeId],
    queryFn: () => candidateService.getCandidatesByOffice(officeId!),
    enabled: !!officeId,
    staleTime: CANDIDATE_STALE_TIME,
    gcTime: CANDIDATE_CACHE_TIME,
  });
}

/**
 * Hook to search candidates
 */
export function useCandidateSearch(query: string) {
  return useQuery({
    queryKey: ['candidates', 'search', query],
    queryFn: () => candidateService.searchCandidates(query),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes for search results
  });
}

/**
 * Hook to prefetch candidate data
 */
export function usePrefetchCandidate() {
  const queryClient = useQueryClient();
  
  return (id: string) => {
    queryClient.prefetchQuery({
      queryKey: ['candidate', id],
      queryFn: () => candidateService.getCandidate(id),
      staleTime: CANDIDATE_STALE_TIME,
    });
  };
}

/**
 * Hook to invalidate candidate cache
 */
export function useInvalidateCandidates() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: ['candidates'] });
  };
}

